import { DigitalTwinSettingCreationRequest, TimeSeriesSettingValue } from 'api/digitalTwin/digitalTwin.api'
import { Dispatch, SetStateAction } from 'react'
import Datetime from 'utils/datetime/datetime'
import { CreateSettingData } from './DigitalTwinSettingDialogContent'

type SettingValue = number | null | TimeSeriesSettingValue | { id?: number; value: number | null }[] | undefined

export type CopySettingAction = {
  label: string
  onClick: () => void
}

type ApplyCopiedDataArgs = {
  copiedData: CreateSettingData
  setCopyKey: Dispatch<SetStateAction<number>>
  setCopiedInitData: Dispatch<SetStateAction<CreateSettingData | null>>
  onCopyChange?: (copy: CreateSettingData | null) => void
}

type DiscardCopiedDataArgs = Omit<ApplyCopiedDataArgs, 'copiedData'>

type GetCopySettingControlsArgs = {
  newSetting: DigitalTwinSettingCreationRequest | null
  isBaseSettings: boolean
  hasAccessToTimeseries: boolean
  hasSettingInstructionsData: boolean
  onApplyCopiedData: (copiedData: CreateSettingData) => void
}

export type CopySettingControls = {
  onCopy?: () => void
  copyActions?: CopySettingAction[]
}

export function applyCopiedData({
  copiedData,
  setCopyKey,
  setCopiedInitData,
  onCopyChange,
}: ApplyCopiedDataArgs): void {
  setCopyKey((prev) => prev + 1)
  setCopiedInitData(copiedData)
  onCopyChange?.(copiedData)
}

export function discardCopiedData({ setCopyKey, setCopiedInitData, onCopyChange }: DiscardCopiedDataArgs): void {
  setCopyKey((prev) => prev + 1)
  setCopiedInitData(null)
  onCopyChange?.(null)
}

export function getCopySettingControls({
  newSetting,
  isBaseSettings,
  hasAccessToTimeseries,
  hasSettingInstructionsData,
  onApplyCopiedData,
}: GetCopySettingControlsArgs): CopySettingControls {
  const handleCopy = (): void => {
    if (!newSetting) return

    onApplyCopiedData(createCopiedSettingData(newSetting))
  }

  const handleCopyToTimeseries = (): void => {
    if (!newSetting) return

    const copiedData = createTimeSeriesCopiedSettingData(newSetting)
    if (!copiedData) return

    onApplyCopiedData(copiedData)
  }

  const canCopyToTimeSeriesSetting =
    !isBaseSettings &&
    !newSetting?.isTimeSeries &&
    !!newSetting?.start_time &&
    !!newSetting?.end_time &&
    hasAccessToTimeseries

  return {
    onCopy: !hasSettingInstructionsData && !isBaseSettings ? handleCopy : undefined,
    copyActions:
      !hasSettingInstructionsData && newSetting && canCopyToTimeSeriesSetting
        ? [
            {
              label: 'Copy as a time series setting',
              onClick: handleCopyToTimeseries,
            },
          ]
        : undefined,
  }
}

export function createCopiedSettingData(setting: DigitalTwinSettingCreationRequest): CreateSettingData {
  return {
    isTimeSeries: setting.isTimeSeries ?? false,
    start_time: setting.start_time,
    end_time: setting.end_time,
    level: setting.level,
    name: setting.name,
    priority: setting.priority,
    comment: setting.comment,
    attribute: setting.attribute,
    value: setting.value as number | null | { id?: number; value: number | null }[] | undefined,
    excluded_priority_layers: setting.excluded_priority_layers,
  }
}

// Time series values come in two different shapes: a positional array `{id?, value}[]` (CreateSettingData,
// as received from the API) and a map keyed by ISO time `{ [time]: {id?, value} }` (DigitalTwinSettingCreationRequest,
// as produced by initializeNewSetting). Normalize both to a plain array of values, ordered by time, so they can be compared.
function normalizeSettingValueForComparison(value: SettingValue): number | null | (number | null)[] {
  if (value === null || value === undefined) {
    return null
  }
  if (Array.isArray(value)) {
    return value.map((entry) => entry?.value ?? null)
  }
  if (typeof value === 'object') {
    return (Object.keys(value) as ISODateTime[]).sort().map((time) => value[time]?.value ?? null)
  }
  return value
}

export function hasSettingValueChanged(newValue: SettingValue, initValue: SettingValue): boolean {
  const normalizedNewValue = normalizeSettingValueForComparison(newValue)
  const normalizedInitValue = normalizeSettingValueForComparison(initValue)

  if (Array.isArray(normalizedNewValue) || Array.isArray(normalizedInitValue)) {
    if (!Array.isArray(normalizedNewValue) || !Array.isArray(normalizedInitValue)) {
      return true
    }
    if (normalizedNewValue.length !== normalizedInitValue.length) {
      return true
    }
    return normalizedNewValue.some((value, index) => value !== normalizedInitValue[index])
  }

  return normalizedNewValue !== normalizedInitValue
}

function sortedLayers(layers: number[] | undefined): number[] {
  return [...(layers ?? [])].sort((a, b) => a - b)
}

/**
 * Whether the form's setting differs from the setting as the form opened it. A time series value is
 * compared hour by hour, and excluded layers regardless of order, with none given counting as none
 * excluded.
 */
export function hasSettingChanged(
  newSetting: DigitalTwinSettingCreationRequest,
  openedSetting: DigitalTwinSettingCreationRequest
): boolean {
  const newLayers = sortedLayers(newSetting.excluded_priority_layers)
  const openedLayers = sortedLayers(openedSetting.excluded_priority_layers)

  return (
    newSetting.level !== openedSetting.level ||
    newSetting.name !== openedSetting.name ||
    newSetting.attribute !== openedSetting.attribute ||
    newSetting.start_time !== openedSetting.start_time ||
    newSetting.end_time !== openedSetting.end_time ||
    newSetting.comment !== openedSetting.comment ||
    newSetting.priority !== openedSetting.priority ||
    newSetting.isTimeSeries !== openedSetting.isTimeSeries ||
    hasSettingValueChanged(newSetting.value, openedSetting.value) ||
    newLayers.length !== openedLayers.length ||
    newLayers.some((layer, index) => layer !== openedLayers[index])
  )
}

export function createTimeSeriesCopiedSettingData(
  setting: DigitalTwinSettingCreationRequest
): CreateSettingData | null {
  if (!setting.start_time || !setting.end_time || (setting.value !== null && typeof setting.value === 'object')) {
    return null
  }

  const scalarValue = typeof setting.value === 'number' ? setting.value : null

  const value = Datetime.getHoursBetween(setting.start_time, setting.end_time).map(() => ({
    value: scalarValue,
  }))

  return {
    ...createCopiedSettingData(setting),
    isTimeSeries: true,
    value,
  }
}
